import Quill, { Delta, EmitterSource, Op, Range } from "quill"
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  MutableRefObject,
} from "react"
import "quill/dist/quill.snow.css"
import "./styles.css"

export type QuillRef = MutableRefObject<Quill | undefined>

type EditorProps = {
  readOnly?: boolean
  defaultValue?: Delta | Op[]
  onTextChange?: (delta: Delta, oldDelta: Delta, source: EmitterSource) => void
  onSelectionChange?: (
    range: Range | null,
    oldRange: Range | null,
    source: EmitterSource,
  ) => void
}

export const Editor = forwardRef<Quill, EditorProps>(
  (
    { readOnly = false, defaultValue, onTextChange, onSelectionChange },
    ref,
  ) => {
    // Этот хак снаровки для доступа к рефу внутри хуков
    const containerRef = useRef<HTMLDivElement | null>(null)

    // Чтобы не ловить замыкание stale-refs
    const defaultValueRef = useRef<Delta | Op[] | undefined>(defaultValue)
    const onTextChangeRef = useRef<EditorProps["onTextChange"]>(onTextChange)
    const onSelectionChangeRef =
      useRef<EditorProps["onSelectionChange"]>(onSelectionChange)

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange
      onSelectionChangeRef.current = onSelectionChange
    }, [onTextChange, onSelectionChange])

    useEffect(() => {
      // Этот ref прокидывается снаружи, его тип — React.MutableRefObject<Quill|null>
      ;(ref as QuillRef).current?.enable(!readOnly)
    }, [ref, readOnly])

    useEffect(() => {
      const container = containerRef.current!
      // Create a child div for the Quill editor
      const editorContainer = container.ownerDocument.createElement("div")
      container.appendChild(editorContainer)

      const quill = new Quill(editorContainer, {
        theme: "snow",
        modules: {
          toolbar: ["bold", "italic", "strike", "underline", "code", "link"],
        },
        formats: ["bold", "italic", "strike", "underline", "code", "link"],
      })

      ;(ref as QuillRef).current = quill

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current)
      }

      quill.on("text-change", (...args) => {
        onTextChangeRef.current?.(args[0], args[1], args[2])
      })

      quill.on("selection-change", (...args) => {
        onSelectionChangeRef.current?.(
          args[0] as Range | null,
          args[1] as Range | null,
          args[2] as EmitterSource,
        )
      })

      return () => {
        ;(ref as QuillRef).current = undefined
        container.innerHTML = ""
      }
    }, [ref])

    return <div ref={containerRef} />
  },
)

Editor.displayName = "Editor"
